import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { v4 as uuidv4 } from "uuid";
import authBgImage from "../../assets/image/authBgImage.jpeg";
import LoginIcon from "../../assets/image/logo.png";
import ButtonLoader from "../../components/loader/ButtonLoader";
import { loginApiAdmin } from "../../redux/auth/slice";
import RippleLoader from "../../components/RippleLoader/RippleLoader";
import { TextField } from "@mui/material";

// Device Type
const getDeviceType = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (/mobile/i.test(userAgent)) {
    return "Mobile";
  } else if (/tablet|ipad|playbook|silk|android(?!.*mobi)/i.test(userAgent)) {
    return "Tablet";
  }
  return "Desktop";
};

// Device Name
const getDeviceName = () => {
  const userAgent = navigator.userAgent;
  if (userAgent.match(/iPhone/i)) return "iPhone";
  if (userAgent.match(/iPad/i)) return "iPad";
  if (userAgent.match(/Android/i)) return "Android Device";
  if (userAgent.match(/Windows/i)) return "Windows PC";
  if (userAgent.match(/Mac/i)) return "Mac";
  return "Unknown Device";
};

const getDeviceID = () => {
  let deviceID = localStorage.getItem("device_id");
  if (!deviceID) {
    deviceID = uuidv4();
    localStorage.setItem("device_id", deviceID);
  }
  return deviceID;
};

function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loginState, setLoginState] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deviceType, setDeviceType] = useState("");
  const [deviceName, setDeviceName] = useState("");
  const [deviceID, setDeviceID] = useState("");
  const storedFcmToken = localStorage.getItem("fcm_token");

  const { logingData, logingDataLoading, logingDataError } = useSelector(
    (store) => ({
      logingData: store?.authData?.loginStatus?.data?.data?.data,
      logingDataLoading: store?.authData?.loading,
      logingDataError: store?.authData?.error,
    })
  );

  useEffect(() => {
    if (!logingDataLoading && !logingDataError && loginState) {
      logingData ? navigate("/") : navigate("/");
    } else {
      navigate("/login");
    }
  }, [navigate, logingDataLoading, logingDataError, loginState, logingData]);

  useEffect(() => {
    setDeviceType(getDeviceType());
    setDeviceName(getDeviceName());
    setDeviceID(getDeviceID());
  }, [dispatch]);

  const handleLogin = (values) => {
    dispatch(
      loginApiAdmin({
        fcm_token: storedFcmToken,
        device_type: deviceType,
        device_name: deviceName,
        device_id: deviceID,
        ...values,
      })
    ).then((res) => {
      setLoginState(false);
      setLoading(false);
      if (res.type === "loginApiAdmin/fulfilled") {
        navigate("/");
      }
    });
  };
  const handleRememberMeChange = (newValue, values) => {
    if (newValue) {
      localStorage.setItem("authEmail", values.email);
      localStorage.setItem("authPassword", values.password);
    } else {
      localStorage.removeItem("authEmail");
      localStorage.removeItem("authPassword");
      localStorage.removeItem("authToken");
    }
  };

  const storedEmail = localStorage.getItem("authEmail");
  const storedPassword = localStorage.getItem("authPassword");
  const storedRememberMe = storedEmail && storedPassword;

  return (
    <>
      <div className={`h-screen overflow-hidden relative`}>
        {logingDataLoading && <RippleLoader />}
        <div
          className="flex items-center justify-center h-full bg-no-repeat bg-cover bg-center bg-black opacity-95"
          style={{
            backgroundImage: `url(${authBgImage})`,
          }}
        >
          <section className="w-full">
            <div className="max-w-xl w-full mx-auto">
              <div className="rounded-lg drop-shadow-lg bg-[#FFFFFFE5] px-8 py-12 mx-4">
                <div className="w-full h-full pb-8">
                  <img
                    src={LoginIcon}
                    alt="logo"
                    className="object-cover w-52"
                  />
                </div>
                <div className={"mb-9"}>
                  <h4 className="text-[34px] font-Montserrat text-darkBlack font-normal mb-1">
                    Welcome!
                  </h4>
                  <p className="text-base mb-1 font-Proxima text-darkBlack font-normal ml-1">
                    To FBK System
                  </p>
                </div>
                {/* Login flow */}
                <Formik
                  initialValues={{
                    email: storedEmail || "",
                    password: storedPassword || "",
                    role: "admin",
                    rememberMe: storedRememberMe || false,
                  }}
                  validationSchema={Yup.object({
                    email: Yup.string().required("Please enter email"),
                    password: Yup.string()
                      .required("Please enter password")
                      .min(
                        8,
                        "Password is too short - should be 8 chars minimum."
                      )
                      .matches(
                        /[a-zA-Z]/,
                        "Password can only contain Latin letters."
                      ),
                  })}
                  onSubmit={(values) => {
                    handleLogin(values);
                    setLoading(true);
                    setLoginState(true);
                  }}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <div className="space-y-7">
                        {/* Email */}
                        <TextField
                          className={`border ${
                            errors.email && touched.email
                              ? "border-red"
                              : values.email
                              ? "border-themeBtn"
                              : "border-gray-300"
                          } w-full `}
                          color="warning"
                          variant="outlined"
                          id="email"
                          label="Email"
                          type="email"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.email}
                          error={errors.email && touched.email}
                          helperText={
                            errors.email && touched.email && errors.email
                          }
                          InputLabelProps={{
                            shrink: values.email ? true : false,
                          }}
                        />
                        {/* Password */}
                        <div className="mb-1">
                          <TextField
                            className={`border ${
                              errors.password && touched.password
                                ? "border-red"
                                : values.password
                                ? "border-themeBtn"
                                : "border-gray-300"
                            } w-full focus:outline-none focus:ring-0`}
                            variant="outlined"
                            color="warning"
                            id="password"
                            label="Password"
                            type="password"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.password}
                            error={errors.password && touched.password}
                            helperText={
                              errors.password &&
                              touched.password &&
                              errors.password
                            }
                            InputLabelProps={{
                              shrink: values.password ? true : false,
                            }}
                          />
                        </div>
                        <div className="py-4 grid grid-cols-2 gap-x-44">
                          <button type="button" className="flex items-center">
                            <input
                              type="checkbox"
                              id={"rememberMe"}
                              name={"Remember Me"}
                              className="w-4 h-4 mr-2 accent-themeBtn bg-white/80"
                              checked={values.rememberMe}
                              onChange={() => {
                                const newValue = !values.rememberMe;
                                setFieldValue("rememberMe", newValue);
                                handleRememberMeChange(newValue, values);
                              }}
                            />
                            <p className="text-primaryBlack font-normal text-base font-Proxima">
                              Remember Me
                            </p>
                          </button>
                          {/* forget password */}
                          <div className="text-right">
                            <Link
                              to={"/reset-password"}
                              className="border-b border-lightOrangeTheme text-themeBtn font-normal text-base font-Proxima"
                            >
                              forget password?
                            </Link>
                          </div>
                        </div>
                        <button
                          type="submit"
                          className="w-full mt-0 block font-Proxima shadow-lg text-white bg-themeBtn text-center font-normal text-base border-2 border-transparent hover:bg-transparent hover:border-2 hover:border-themeBtn hover:text-themeBtn rounded transition duration-500 p-2 lg:px-7 px-4"
                        >
                          {loading ? <ButtonLoader /> : "LOGIN"}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
              <p className="text-bgWhite font-Proxima text-base font-normal text-center mt-2">
                FBK Group Pte Ltd
              </p>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

export default Login;
